// =============================================================================
// Auth Callback Page
// Landing page for Cognito's OAuth2 redirect (/auth/callback?code=...&state=...)
// =============================================================================
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/store/hooks';
import { initializeAuth } from '@/store/slices/authSlice';
import { oauthCallbackPromise } from '@/lib/oauthCallback';
import { PLUGIN_POPUP_PENDING_KEY, SYMBIOTE_POPUP_PENDING_KEY } from '@/lib/authHandoff';

// Timeout before giving up and redirecting to the login error page.
const CALLBACK_TIMEOUT_MS = 30_000;

/**
 * Awaits the oauthCallbackPromise, which was registered at module load time
 * (before Amplify.configure() runs) to guarantee the Hub event is never missed.
 *
 * Two outcomes:
 *   signInWithRedirect         → hydrate Redux auth state, navigate to app.
 *   signInWithRedirect_failure → navigate to login with an error flag.
 *
 * A 30-second timeout covers the edge case where the exchange hangs and
 * neither Hub event ever fires.
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    let cancelled = false;

    const timeout = setTimeout(() => {
      if (!cancelled) {
        cancelled = true;
        navigate('/login?error=auth_failed', { replace: true });
      }
    }, CALLBACK_TIMEOUT_MS);

    oauthCallbackPromise.then(async ({ event, errorMessage }) => {
      clearTimeout(timeout);
      if (cancelled) return;
      cancelled = true;

      if (event === 'signInWithRedirect') {
        await dispatch(initializeAuth());
        // If this OAuth flow was triggered by the browser extension popup, redirect
        // back to /auth/plugin?popup=1 so PluginAuthPage can extract the token and
        // broadcast it to the waiting iframe. The flag was set in handleDiscordLogin.
        const symbiotePending = localStorage.getItem(SYMBIOTE_POPUP_PENDING_KEY);
        const isPluginPopup = localStorage.getItem(PLUGIN_POPUP_PENDING_KEY) === '1';
        if (symbiotePending) {
          localStorage.removeItem(SYMBIOTE_POPUP_PENDING_KEY);
          try {
            const { nonce } = JSON.parse(symbiotePending) as { nonce?: string };
            if (!nonce) throw new Error('missing nonce');
            navigate(`/auth/plugin?symbiote=1&nonce=${encodeURIComponent(nonce)}`, { replace: true });
          } catch {
            navigate('/login?error=auth_failed&detail=Invalid%20TaleSpire%20authorization%20state', { replace: true });
          }
        } else if (isPluginPopup) {
          localStorage.removeItem(PLUGIN_POPUP_PENDING_KEY);
          navigate('/auth/plugin?popup=1', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } else {
        // Encode the Amplify error message into the URL so it's visible in the
        // address bar and on the login page — useful for diagnosing the root cause.
        console.error('[AuthCallback] OAuth exchange failed:', errorMessage);
        const params = new URLSearchParams({ error: 'auth_failed' });
        if (errorMessage) params.set('detail', errorMessage);
        navigate(`/login?${params}`, { replace: true });
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [dispatch, navigate]);

  return (
    <div className="flex items-center justify-center h-screen bg-stone-900">
      <div className="text-center space-y-3">
        <p className="text-parchment font-display text-2xl">Signing you in…</p>
        <p className="text-stone-400 text-sm">Please wait while we verify your identity.</p>
      </div>
    </div>
  );
}
